/**
 * Limits View
 */

define(function(require) {
    var _ = require('underscore');
    var BaseView = require('common/base.view');
    var Form = require('class/form.class');
    const serverUtil = require('util/server.util');
    var userValidationConfig = require('modules/user/user.validation.config');
    var LimitsModel = require('portlets/responsible-gaming/limits/limits.model');
    var validationHelpers = require('modules/user/user.validation.helpers');

    return BaseView.extend({

        events: {
            'focusout .fn-deposit-limits .fn-limit input': 'validateDepositLimitsAscendingOrder',
            'change .fn-deposit-limits .fn-limit select': 'validateDepositLimitsAscendingOrder',

            'submit .fn-form-session-timer': 'updateSessionTimer',
            'submit .fn-deposit-limits': 'updateDepositLimits',
            'tap .fn-confirm-deposit-limit': 'confirmDepositLimit'
        },

        initialize: function(options) {
            this.options = options;
            this.model = new LimitsModel(this.options.config);
        },

        subscribe: function() {
            this.listenTo(this.model, 'change', this.render);
        },

        render: function() {
            this.$el.html(this.tmpl('limits', this.model.toJSON()));

            var $formSessionTimer = this.$('.fn-form-session-timer');
            if ($formSessionTimer.length) {
                this.formSessionTimer = new Form($formSessionTimer);
            }

            var $formDepositLimits = this.$('.fn-deposit-limits');
            if ($formDepositLimits.length) {
                this.formDepositLimits = new Form($formDepositLimits, {
                    validationConfig: userValidationConfig(),
                    data: this.getFormData()
                });
            }

            this.validateAllLimitsAscendingOrder();

            return this;
        },

        getFormData: function() {
            var cfg = this.model.get('depositConfig');
            var allowedLimits = this.model.get('depositLimits').allowedLimits || this.model.get('allowedLimits');

            return {
                allowedLimits: allowedLimits,
                predefinedType: cfg.predefinedType,
                playerMustSetLimits: cfg.playerMustSetLimits,
                allPeriodsRequired: cfg.allPeriodsRequired,
                noLimitsAllowed: cfg.noLimitsAllowed,
                UNLIMITED_AMOUNT: cfg.UNLIMITED_AMOUNT
            };
        },

        updateDepositLimits: function(e) {
            e.preventDefault();

            this.formDepositLimits.hideErrorMessages();

            if (!this.hasToShowQuestionnaire(this.formDepositLimits.selectedData) && !this.formDepositLimits.isDataChanged()) {
                this.showInfo({
                    content: this.translate('playtech.mobile.ngresponsiblegaming.limits.RGLimits.submitInitialFormData'),
                    variant: this.toastVariants.INFO
                });

                return;
            }

            this.formDepositLimits.validate().done(function(data, form) {
                form.showLoading();
                this.hideKeyboard();

                if (this.hasToShowQuestionnaire(data)) {
                    Playtech.API.popup.showPopup('questionnaire', {
                        ...data,
                        formName: this.model.get('questionnaireFormName'),
                        title: 'playtech.mobile.ngresponsiblegaming.questionnaire.policy',
                        tkPrefix: 'playtech.mobile.ngresponsiblegaming.questionnaire.',
                        onClose: form.hideLoading.bind(form),
                        onSuccess: function() {
                            this.model.updateDepositLimits(data)
                                .done(this.successUpdateLimits.bind(this, data))
                                .fail(this.errorUpdateLimits.bind(this))
                                .always(form.hideLoading.bind(form));
                        }.bind(this)
                    });
                } else {
                    this.model.updateDepositLimits(data)
                        .done(this.successUpdateLimits.bind(this, data))
                        .fail(this.errorUpdateLimits.bind(this))
                        .always(form.hideLoading.bind(form));
                }
            }.bind(this));
        },

        hasToShowQuestionnaire: function(data) {
            if (!this.model.get('depositLimitsQuestionnaire')) {
                // questionnaire is not configured
                return false;
            }

            const { waitingLimits } = this.model.get('fetchedDepositLimits');

            if (waitingLimits?.length) {
                // if already has pending limits, no need to show questionnaire
                return false;
            }

            return this.compareDayLimits(data) || this.compareWeekLimits(data)
                || this.compareMonthLimits(data);
        },

        compareDayLimits: function(formData) {
            const { dailyDefaultLimit } = this.model.get('predefinedLimits');

            const {
                currentDayDepositLimit,
                waitingdaydepositlimit,
                lastIncreaseOverDefaultDate
            } = this.model.get('fetchedDepositLimits');

            const [daydepositlimit, dailyPredefinedLimit, dailyCurrentLimit] = [
                formData.daydepositlimit || 0,
                dailyDefaultLimit || 0,
                currentDayDepositLimit && currentDayDepositLimit.amount && currentDayDepositLimit.amount.amount || 0
            ].map(Number);

            const compareStartDateDayLimits = lastIncreaseOverDefaultDate
                && this.isThreeMonthHavePassed(lastIncreaseOverDefaultDate);

            return !waitingdaydepositlimit && (!lastIncreaseOverDefaultDate || compareStartDateDayLimits)
                && daydepositlimit > dailyPredefinedLimit
                && daydepositlimit > dailyCurrentLimit;
        },

        compareWeekLimits: function(formData) {
            const { weeklyDefaultLimit } = this.model.get('predefinedLimits');

            const {
                currentWeekDepositLimit,
                waitingweekdepositlimit,
                lastIncreaseOverDefaultDate
            } = this.model.get('fetchedDepositLimits');

            const [weekdepositlimit, weeklyPredefinedLimit, weeklyCurrentLimit] = [
                formData.weekdepositlimit || 0,
                weeklyDefaultLimit || 0,
                currentWeekDepositLimit && currentWeekDepositLimit.amount && currentWeekDepositLimit.amount.amount || 0
            ].map(Number);

            const compareStartDateWeekLimits = lastIncreaseOverDefaultDate
                && this.isThreeMonthHavePassed(lastIncreaseOverDefaultDate);

            return !waitingweekdepositlimit && (!lastIncreaseOverDefaultDate || compareStartDateWeekLimits)
                && weekdepositlimit
                > weeklyPredefinedLimit && weekdepositlimit > weeklyCurrentLimit;
        },

        compareMonthLimits: function(formData) {
            const { monthlyDefaultLimit } = this.model.get('predefinedLimits');

            const {
                currentMonthDepositLimit,
                waitingmonthdepositlimit,
                lastIncreaseOverDefaultDate
            } = this.model.get('fetchedDepositLimits');

            const [monthdepositlimit, monthlyPredefinedLimit, monthlyCurrentLimit] = [
                formData.monthdepositlimit || 0,
                monthlyDefaultLimit || 0,
                currentMonthDepositLimit && currentMonthDepositLimit.amount && currentMonthDepositLimit.amount.amount
                || 0
            ].map(Number);

            const compareStartDateMonthLimits = lastIncreaseOverDefaultDate
                && this.isThreeMonthHavePassed(lastIncreaseOverDefaultDate);

            return !waitingmonthdepositlimit && (!lastIncreaseOverDefaultDate || compareStartDateMonthLimits)
                && monthdepositlimit
                > monthlyPredefinedLimit && monthdepositlimit > monthlyCurrentLimit;
        },

        isThreeMonthHavePassed: function(startDate) {
            let count = 0;
            const curDate = window.Playtech.API.formatter.parseImsDate(startDate);
            const endDate = new Date();

            while (curDate.toISOString() <= endDate.toISOString()) {
                var dayOfWeek = curDate.getDay();

                if (!(dayOfWeek === 6 || dayOfWeek === 0)) {
                    count++;
                }

                curDate.setDate(curDate.getDate() + 1);
            }

            return count >= 65; // always 65 business days in three month
        },

        successUpdateLimits: function(data) {
            this.model.set(data);

            this.model.fetchInfo()
                .done(function() {
                    this.render();
                }.bind(this))
                .fail(this.renderNetworkError.bind(this));

            this.model.fetchDepositLimitInfo()
                .done(function() {
                    this.render();

                    this.showInfo({
                        content: this.translate('playtech.mobile.ngresponsiblegaming.limits.successUpdate'),
                        variant: this.toastVariants.SUCCESS
                    });
                }.bind(this))
                .fail(this.renderNetworkError.bind(this));
        },

        errorUpdateLimits: function(response) {
            this.render();

            const errorMessage = serverUtil.getErrorMessage(response, {
                tkPrefix: 'playtech.mobile.ngresponsiblegaming.limits.error.',
                defaultMessage: this.translate('playtech.mobile.ngresponsiblegaming.limits.error.default')
            });

            this.formDepositLimits.showErrorMessage({ content: errorMessage });
        },

        updateSessionTimer: function(e) {
            e.preventDefault();

            this.formSessionTimer.hideErrorMessages();

            var form = this.formSessionTimer;
            var data = form.data();

            if (form.selectedData.sessionduration === data.sessionduration) {
                this.showInfo({
                    content: this.translate('playtech.mobile.ngresponsiblegaming.sessionTimer.submitInitialFormData'),
                    variant: this.toastVariants.INFO
                });

                return;
            }

            form.selectedData = _.clone(data);
            form.showLoading();

            this.model.updateSessionTimer(data)
                .done(this.successUpdateSessionTimer.bind(this, data))
                .fail(this.errorUpdateSessionTimer.bind(this))
                .always(form.hideLoading.bind(form));
        },

        successUpdateSessionTimer: function(data) {
            var sessionduration = Number(this.model.get('sessionduration'));
            var successMessage;

            this.model.set(data);

            if (sessionduration) {
                successMessage = this.translate('playtech.mobile.ngresponsiblegaming.limits.activated', sessionduration);
            } else {
                successMessage = this.translate('playtech.mobile.ngresponsiblegaming.limits.deactivated');
            }

            this.showInfo({
                content: successMessage,
                variant: this.toastVariants.SUCCESS
            });
        },

        errorUpdateSessionTimer: function(response) {
            this.render();

            if (response?.data?.errorCode === 1915 || response?.data?.errorCode === 1920) {
                this.model.set('sessionduration', this.model.get('waitingsessionduration'));
            }

            const errorMessage = serverUtil.getErrorMessage(response, {
                tkPrefix: 'playtech.mobile.ngresponsiblegaming.limits.error.'
            });

            this.formSessionTimer.showErrorMessage({ content: errorMessage });
        },

        confirmDepositLimit: function(event) {
            const { action, timePeriod } = event.target.dataset;
            this.$('.fn-pending-limits-messages').html('').addClass('hidden');

            if (!action || !timePeriod) {
                return;
            }

            this.model.acceptPlayerDepositLimits(action, timePeriod)
                .then(() => {
                    this.showInfo({
                        content: this.translate(`playtech.mobile.system.responsibleGaming.limits.${action}Success`),
                        variant: this.toastVariants.SUCCESS
                    });
                    this.model.fetchDepositLimitInfo();
                })
                .catch((error) => {
                    const errorMessage = serverUtil.getErrorMessage(error, {
                        tkPrefix: 'playtech.mobile.ngresponsiblegaming.limits.error.',
                        defaultMessage: this.translate('playtech.mobile.ngresponsiblegaming.limits.generic.error')
                    });

                    this.$('.fn-pending-limits-messages').html(errorMessage).removeClass('hidden');
                });
        },

        getLimitsValidationData: function(formData) {
            return Object.fromEntries(
                Object.entries(formData)
                    .map(([key, value]) => [this.getLimitName(key), value])
                    .filter(([key, value]) => key && Number(String(value).replace(',', '.')))
                    .map(([key, value]) => [key, Number(String(value).replace(',', '.'))])
            );
        },

        getLimitName: function(fieldName) {
            var validationFieldTypesMatchStr = 'session|day|week|month';

            return fieldName?.match(validationFieldTypesMatchStr)?.[0];
        },

        validateDepositLimitsAscendingOrder: function() {
            this.validateLimitsAscendingOrder(this.formDepositLimits);
        },

        validateLimitsAscendingOrder: function(form) {
            const validationData = this.getLimitsValidationData(form.data());
            const validations = Object.keys(validationData)
                .map((limitName) => validationHelpers.isAscendingLimitAmount[limitName])
                .filter(Boolean);

            if (!validations.length) {
                return;
            }

            const allValid = validations.every((validation) => validation(validationData));

            if (allValid) {
                form.hideWarningMessages();
            } else {
                form.showWarningMessage({ translation: 'playtech.mobile.system.tooltip.limits.ascending.warning' });
            }
        },

        validateAllLimitsAscendingOrder: function() {
            if (this.formDepositLimits) {
                this.validateLimitsAscendingOrder(this.formDepositLimits);
            }
        }
    });
});
